import type { LearningLanguage } from '@/entities/user';

interface Bundle {
  recognizingVoice: string;
  thinking: string;
  checking: string;
  explain: string;
  improve: string;
  sheetTitle: string;
  /** Текст разбора — на языке интерфейса, но ошибка в сценарии у каждого
   *  изучаемого языка своя, поэтому ключи — изучаемые языки. */
  explanation: Record<LearningLanguage, string>;
}

/**
 * Строки интерфейса демо — дословно те же, что в настоящем чате (см.
 * `MessageBubble.i18n.ts`, `ChatScreen.i18n.ts`, `ExplainSheet.i18n.ts`):
 * демо показывает реальный экран, а не пересказ. Они живут на языке
 * интерфейса, как и весь остальной UI.
 *
 * Реплики диалога (вопрос тьютора, реплика ученика, правило) — наоборот, на
 * изучаемом языке, который выбран первым шагом онбординга: см.
 * `ProductDemoScript.ts`.
 */
export default {
  en: {
    recognizingVoice: 'Recognizing voice…',
    thinking: 'Thinking',
    checking: 'Checking',
    explain: 'Explain',
    improve: 'Improve',
    sheetTitle: 'Error breakdown',
    explanation: {
      en: '"go" describes a repeated or general action. For something that happened yesterday — a finished, one-time event — English needs the past form: "went".',
      de: '"gehe" describes a repeated or general action. For something that happened yesterday — a finished, one-time event — German needs the past form: "ging".',
      pt: '"vou" is the present tense. For something that happened yesterday — a finished, one-time event — Portuguese needs the past form: "fui".',
    },
  },
  ru: {
    recognizingVoice: 'Распознаю голос…',
    thinking: 'Думаю',
    checking: 'Проверяю',
    explain: 'Объяснить',
    improve: 'Улучшить',
    sheetTitle: 'Объяснение ошибок',
    explanation: {
      en: '«go» описывает повторяющееся или общее действие. Для события вчера — законченного, однократного — в английском нужна форма прошедшего времени: «went».',
      de: '«gehe» описывает повторяющееся или общее действие. Для события вчера — законченного, однократного — в немецком нужна форма прошедшего времени: «ging».',
      pt: '«vou» — настоящее время. Для события вчера — законченного, однократного — в португальском нужна форма прошедшего времени: «fui».',
    },
  },
  de: {
    recognizingVoice: 'Spracherkennung…',
    thinking: 'Denke nach',
    checking: 'Prüfe',
    explain: 'Erklären',
    improve: 'Verbessern',
    sheetTitle: 'Fehleranalyse',
    explanation: {
      en: '„go“ beschreibt eine wiederholte oder allgemeine Handlung. Für etwas, das gestern passiert ist — ein abgeschlossenes, einmaliges Ereignis — braucht Englisch die Vergangenheitsform: „went“.',
      de: '„gehe“ beschreibt eine wiederholte oder allgemeine Handlung. Für etwas, das gestern passiert ist — ein abgeschlossenes, einmaliges Ereignis — braucht Deutsch die Vergangenheitsform: „ging“.',
      pt: '„vou“ ist die Gegenwartsform. Für etwas, das gestern passiert ist — ein abgeschlossenes, einmaliges Ereignis — braucht Portugiesisch die Vergangenheitsform: „fui“.',
    },
  },
  pt: {
    recognizingVoice: 'Reconhecendo a voz…',
    thinking: 'Pensando',
    checking: 'Verificando',
    explain: 'Explicar',
    improve: 'Melhorar',
    sheetTitle: 'Explicação dos erros',
    explanation: {
      en: '“go” descreve uma ação repetida ou geral. Para algo que aconteceu ontem — um evento concluído, único — o inglês precisa da forma de passado: “went”.',
      de: '“gehe” descreve uma ação repetida ou geral. Para algo que aconteceu ontem — um evento concluído, único — o alemão precisa da forma de passado: “ging”.',
      pt: '“vou” está no presente. Para algo que aconteceu ontem — um evento concluído, único — o português precisa da forma de passado: “fui”.',
    },
  },
  fr: {
    recognizingVoice: 'Reconnaissance vocale…',
    thinking: 'Réflexion',
    checking: 'Vérification',
    explain: 'Expliquer',
    improve: 'Améliorer',
    sheetTitle: 'Explication des erreurs',
    explanation: {
      en: '« go » décrit une action répétée ou générale. Pour quelque chose qui s’est passé hier — un événement terminé, ponctuel — l’anglais exige la forme au passé : « went ».',
      de: '« gehe » décrit une action répétée ou générale. Pour quelque chose qui s’est passé hier — un événement terminé, ponctuel — l’allemand exige la forme au passé : « ging ».',
      pt: '« vou » est au présent. Pour quelque chose qui s’est passé hier — un événement terminé, ponctuel — le portugais exige la forme au passé : « fui ».',
    },
  },
  es: {
    recognizingVoice: 'Reconociendo la voz…',
    thinking: 'Pensando',
    checking: 'Comprobando',
    explain: 'Explicar',
    improve: 'Mejorar',
    sheetTitle: 'Explicación de los errores',
    explanation: {
      en: '“go” describe una acción repetida o general. Para algo que pasó ayer — un evento terminado, puntual — el inglés necesita la forma de pasado: “went”.',
      de: '“gehe” describe una acción repetida o general. Para algo que pasó ayer — un evento terminado, puntual — el alemán necesita la forma de pasado: “ging”.',
      pt: '“vou” está en presente. Para algo que pasó ayer — un evento terminado, puntual — el portugués necesita la forma de pasado: “fui”.',
    },
  },
} satisfies Record<'en' | 'ru' | 'de' | 'pt' | 'fr' | 'es', Bundle>;
